import type { TeacherAvailability } from '@/types/teacher-request'

interface TeacherPriorityCardProps {
  teacher: TeacherAvailability
  priorityRank: number
  onToggle: () => void
  timeSlotsCount: number
  selectedDate: string
  disabled?: boolean
  requestMode?: 'sequential' | 'parallel'
  backgroundName?: string
}

export function TeacherPriorityCard({
  teacher,
  priorityRank,
  onToggle,
  timeSlotsCount,
  selectedDate,
  disabled = false,
  requestMode: _requestMode = 'sequential',
  backgroundName,
}: TeacherPriorityCardProps) {
  const freeSlots = teacher.available_slot_ids?.length || 0
  const blockedSlots = teacher.blocked_slot_ids?.length || 0
  const blockedNames = (teacher.blocked_slot_names || []).sort()

  const showPriority = priorityRank > 0

  let borderColor = 'border-l-gray-200'
  let badgeColor = ''
  let badgeText = ''
  let bgColor = 'bg-white'

  if (showPriority) {
    if (priorityRank === 1) {
      borderColor = 'border-l-green-500'
      badgeColor = 'bg-green-500'
      badgeText = 'text-white'
      bgColor = 'bg-green-50'
    } else if (priorityRank === 2) {
      borderColor = 'border-l-orange-500'
      badgeColor = 'bg-orange-500'
      badgeText = 'text-white'
      bgColor = 'bg-orange-50'
    } else if (priorityRank > 2) {
      borderColor = 'border-l-yellow-400'
      badgeColor = 'bg-yellow-400'
      badgeText = 'text-gray-900'
      bgColor = 'bg-yellow-50'
    }
  }

  const getQualificationBadge = () => {
    switch (teacher.qualification_type) {
      case 'qualified':
        return { color: 'bg-green-100 text-green-700', text: 'Qualifiziert' }
      case 'group':
        return { color: 'bg-blue-100 text-blue-700', text: teacher.group_name || 'Gruppe' }
      case 'unknown':
        return { color: 'bg-yellow-100 text-yellow-700', text: 'Keine Qualifikationsdaten' }
      case 'none':
        return { color: 'bg-red-100 text-red-700', text: 'Nicht qualifiziert' }
      default:
        return null
    }
  }

  const qualBadge = getQualificationBadge()

  const isPartiallyAvailable = freeSlots > 0 && freeSlots < timeSlotsCount

  return (
    <div
      onClick={disabled ? undefined : onToggle}
      className={`rounded-xl shadow-sm border-l-4 p-4 ${borderColor} ${bgColor} ${
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:shadow-md active:scale-[0.98]'
      } transition-all relative`}
    >
      {disabled && teacher.blocking_reason && (
        <div className="absolute top-2 left-2 right-2 bg-red-50 border border-red-200 rounded px-2 py-1 text-xs text-red-700">
          {teacher.blocking_reason}
        </div>
      )}

      {showPriority && (
        <div className={`absolute top-3 right-3 w-7 h-7 rounded-full ${badgeColor} flex items-center justify-center`}>
          <span className={`text-sm font-bold ${badgeText}`}>{priorityRank}</span>
        </div>
      )}



      <div className={`font-medium ${disabled ? 'pt-8' : 'pr-10'}`}>{teacher.full_name} ({teacher.teacher_code})</div>
      <div className="text-sm text-gray-500 mt-1">
        Annahme-Quote: {teacher.accept_decline_ratio?.toFixed(0)}%
      </div>

      {/* Qualification badge */}
      {qualBadge && (
        <div className="mt-1">
          <span className={`inline-block text-xs px-2 py-0.5 rounded ${qualBadge.color}`}>
            {qualBadge.text}
          </span>
        </div>
      )}

      {/* Background name */}
      {backgroundName && (
        <div className="mt-2 text-xs text-blue-600 font-medium">
          Fach: {backgroundName}
        </div>
      )}

      {/* Partially available status */}
      {isPartiallyAvailable && (
        <div className="text-xs text-orange-600 mt-1 font-medium">
          Teilweise verfügbar ({freeSlots}/{timeSlotsCount} Slots)
        </div>
      )}

      {/* Qualified for subjects */}
      {teacher.qualified_for_subjects && teacher.qualified_for_subjects.length > 0 && (
        <div className="text-xs text-gray-500 mt-1">
          Faecher: {teacher.qualified_for_subjects.join(', ')}
        </div>
      )}

      {/* Blocking reason - more prominent */}
      {teacher.blocking_reason && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 font-medium">
          {teacher.blocking_reason}
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-gray-100">
        <div className="text-sm font-medium text-gray-700">Verfugbarkeit am {selectedDate}</div>

        <div className="mt-2 flex items-center gap-4 text-sm">
          <span className="text-green-600">
            ✓ {freeSlots} frei
          </span>
          <span className="text-red-500">
            ✗ {blockedSlots} belegt
          </span>
          <span className="text-gray-400">
            ({timeSlotsCount} total)
          </span>
        </div>

        {blockedNames.length > 0 && (
          <div className="mt-2 text-xs text-gray-500">
            Belegt: {blockedNames.join(', ')}
          </div>
        )}
      </div>

      {showPriority && (
        <div className="mt-3 pt-2 border-t border-gray-100">
          <span className={`text-xs font-medium ${
            priorityRank === 1 ? 'text-green-600' :
            priorityRank === 2 ? 'text-orange-600' :
            'text-yellow-600'
          }`}>
            Prioritat {priorityRank}
          </span>
        </div>
      )}


    </div>
  )
}